import { models, stores } from '@kurtosys/app-start';
import { models as eventManagerModels } from '@kurtosys/event-manager';
import { EventTypes } from '@kurtosys/event-manager/dist/models/EventTypes.js';
import { common, query } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IRedirect } from '@kurtosys/types/helpers/redirect/IRedirect.js';
import { action, computed, makeObservable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration.js';
import { EventBusDetail } from '../../../models/app/EventBusDetail.js';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { ApplyFiltersStore } from '../../ApplyFilters/stores/ApplyFiltersStore.js';
import { ChangeAttestationStore } from '../../ChangeAttestation/stores/ChangeAttestationStore.js';
import { ClearAllFiltersStore } from '../../ClearAllFilters/stores/ClearAllFiltersStore.js';
import { DatePickerStore } from '../../DatePicker/stores/DatePickerStore.js';
import { DropdownStore } from '../../Dropdown/stores/DropdownStore.js';
import { MegaMenuStore } from '../../MegaMenu/stores/MegaMenuStore.js';
import { MenuStore } from '../../Menu/stores/MenuStore.js';
import { DEFAULT_EVENT_BUS_ID } from '../../shared/constants.js';
import { Feature } from '../../shared/Feature.js';
import { IAppComponents } from '../models/IAppComponents.js';
import { IAppStorage } from '../models/IAppStorage.js';
import { IEventBusOptions } from '../models/IEventBusOptions.js';
import { IGlobalSearchOptions } from '../models/IGlobalSearchOptions.js';
import { IInputConfiguration } from '../models/IInputConfiguration.js';
import { InputType } from '../models/InputType.js';

import { PropertyMappingStore } from './clientConfigurations/PropertyMappingStore.js';
import { ComponentInputsStore } from './ComponentsInputsStore.js';
import { FilterPropertyStore } from './FilterPropertyStore.js';
import { GlobalInputsStore } from './GlobalInputsStore.js';
/* [Component: appStoreComponentImport] */

type EventGlobalSearchChangeDetail = eventManagerModels.detail.EventGlobalSearchChangeDetail;
type EventGlobalSearchPublish = eventManagerModels.EventPublish<EventGlobalSearchChangeDetail>;

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	static EVENT_BUS_KEY: EventTypes = EventTypes.GLOBAL_INPUTS_CHANGE;
	url: URL;

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig);
		this.url = new URL(url);
		makeObservable(this);
	}

	@computed
	get filterPropertyStore() {
		return this.storeContext.get<FilterPropertyStore>('filterPropertyStore');
	}

	get hasData(): boolean {
		let hasAppData = false;
		for (const input of this.inputConfigurations) {
			const datePicker = input.type === 'DatePicker' && input.options && input.options.datePicker;
			const dropdown = input.type === 'Dropdown' && input.options && input.options.dropdown;
			const menu = input.type === 'Menu' && input.options && input.options.menu;
			const megaMenu = input.type === 'MegaMenu' && input.options && input.options.megaMenu;
			const shouldHideDatePicker =
				datePicker && (!datePicker.noDataOptions || datePicker.noDataOptions === 'hideApp');
			const shouldHideDropdown = dropdown && (!dropdown.noDataOptions || dropdown.noDataOptions === 'hideApp');
			const shouldHideMenu = menu && (!menu.noDataOptions || menu.noDataOptions === 'hideApp');
			const shouldHideMegaMenu = megaMenu && (!megaMenu.noDataOptions || megaMenu.noDataOptions === 'hideApp');

			if (datePicker) {
				const allowedValues = datePicker.allowedValues;
				if (allowedValues) {
					const datePickerStore = this.getDatePickerStore(input.key);
					if (datePickerStore) {
						if (datePickerStore.hasData) {
							hasAppData = true;
						} else if (shouldHideDatePicker) {
							hasAppData = false;
							break;
						}
					}
				} else {
					hasAppData = true;
				}
			} else if (dropdown) {
				const dropdownStore = this.getDropdownStore(input.key);
				if (dropdownStore) {
					if (dropdownStore.hasData) {
						hasAppData = true;
					} else if (shouldHideDropdown) {
						hasAppData = false;
						break;
					}
				}
			} else if (menu) {
				const menuStore = this.getMenuStore(input.key);
				if (menuStore.hasData) {
					hasAppData = true;
				} else if (shouldHideMenu) {
					hasAppData = false;
					break;
				}
			} else if (megaMenu) {
				const megaMenuStore = this.getMegaMenuStore(input.key);
				if (megaMenuStore.hasData) {
					hasAppData = true;
				} else if (shouldHideMegaMenu) {
					hasAppData = false;
					break;
				}
			}
		}
		return hasAppData;
	}

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	};

	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	getDatePickerStore(inputKey: string): DatePickerStore | undefined {
		const { datePickerStores } = this.storeContext.get<ComponentInputsStore>('componentInputsStore');
		return datePickerStores.get(inputKey);
	}

	getDropdownStore(inputKey: string): DropdownStore | undefined {
		const { dropdownStores } = this.storeContext.get<ComponentInputsStore>('componentInputsStore');
		return dropdownStores.get(inputKey);
	}

	getMenuStore(inputKey: string): MenuStore | undefined {
		const { menuStores } = this.storeContext.get<ComponentInputsStore>('componentInputsStore');
		return menuStores.get(inputKey);
	}

	getMegaMenuStore(inputKey: string): MegaMenuStore | undefined {
		const { megaMenuStores } = this.storeContext.get<ComponentInputsStore>('componentInputsStore');
		return megaMenuStores.get(inputKey);
	}

	getChangeAttestationStore(inputKey: string): ChangeAttestationStore | undefined {
		const { changeAttestationStores } = this.storeContext.get<ComponentInputsStore>('componentInputsStore');
		return changeAttestationStores.get(inputKey);
	}

	getApplyFiltersStore(inputKey: string): ApplyFiltersStore | undefined {
		const { applyFiltersStores } = this.storeContext.get<ComponentInputsStore>('componentInputsStore');
		return applyFiltersStores.get(inputKey);
	}

	getClearAllFiltersStore(inputKey: string): ClearAllFiltersStore | undefined {
		const { clearAllFiltersStores } = this.storeContext.get<ComponentInputsStore>('componentInputsStore');
		return clearAllFiltersStores.get(inputKey);
	}

	@computed
	get redirectHelper(): common.helpers.RedirectHelper | undefined {
		const redirectConfiguration = this.redirectConfiguration;
		if (redirectConfiguration) {
			const { defaultUrl, base } = redirectConfiguration;
			return new common.helpers.RedirectHelper(
				{ transformClass: query.transform.Transform, queryClass: query.Query },
				defaultUrl,
				base,
			);
		}
	}

	@computed
	get hasApplyFiltersButton() {
		return this.inputConfigurations.some((i) => i.type === InputType.ApplyFilters);
	}

	@computed
	get inputConfigurations(): IInputConfiguration[] {
		return (this.appComponentConfiguration && this.appComponentConfiguration.inputs) || [];
	}

	@computed
	get globalSearchOptions(): IGlobalSearchOptions | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.globalSearchOptions;
	}

	@computed
	get storage(): IAppStorage | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.storage;
	}

	@computed
	get eventBusOptions(): IEventBusOptions | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.eventBusOptions;
	}

	@computed
	get redirectConfiguration(): IRedirect | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.redirect;
	}

	@computed
	get eventBusIdentifier(): string {
		const defaultValue = DEFAULT_EVENT_BUS_ID;
		if (this.eventBusOptions) {
			return this.eventBusOptions.identifier || defaultValue;
		}
		return defaultValue;
	}

	@computed
	get eventBusInclude(): EventBusDetail[] {
		if (this.eventBusOptions) {
			return this.eventBusOptions.include || [];
		}
		return [];
	}

	@computed
	get loadInitialValuesFromStorage(): boolean {
		return !(this.appComponentConfiguration && this.appComponentConfiguration.disableInitialStorageValues);
	}

	/**
	 * Override AppStoreBase to prevent circular event triggering on nested global inputs app
	 *
	 * @readonly
	 */
	get globalInputIdentifiers(): string[] {
		const identifiers =
			(this.appComponentConfiguration && this.appComponentConfiguration.globalInputIdentifiers) || [];
		return identifiers.filter((id) => id !== this.eventBusIdentifier);
	}

	/**
	 * Overrides AppStoreBase.handleSearchTermUpdate
	 *
	 */
	public handleSearchTermUpdate = async (ev: EventGlobalSearchPublish) => {
		this.onBeforeSearchTermUpdate(ev);
		this.filterPropertyStore.setSearchTerm(ev.detail.searchTerm);
		this.onAfterSearchTermUpdate(ev);
	};

	async customInitializeAfter(): Promise<void> {
		await this.storeContext.get<PropertyMappingStore>('propertyMappingStore').initialize();
		await this.storeContext.get<GlobalInputsStore>('globalInputsStore').initialize();
		await this.storeContext.get<FilterPropertyStore>('filterPropertyStore').initialize();
		await this.storeContext.get<ComponentInputsStore>('componentInputsStore').initialize();
	}
}
